/*
 * How the Comprehensive Wellness Score is worked out, live: five step tabs
 * over one large stage (points → body systems → weights → lifestyle →
 * category), and the "What if we change some findings?" switches beside it.
 * Every number comes from scoreModel; nothing is hard-coded here.
 */
import { useState } from 'react';
import ScoreRing from '../../mocks/ScoreRing';
import { BarsGlyph, LiverGlyph, PersonGlyph, PillBarsGlyph, TestTubeGlyph } from '../../mocks/ProductGlyphs';
import { ADJUSTMENTS, computeScore } from './scoreModel';
import {
  BandBar,
  DEFAULT_FINDINGS,
  DEFAULT_RESULT,
  Ladder,
  PtsBadge,
  TIER_TONE,
  TierWord,
  one,
  signed,
} from './HealthScoreParts';

const SCREEN = 'product.health-scores';

const SWITCH_GROUPS = [
  {
    name: 'Findings',
    items: [
      { key: 'iron', id: 'toggle-iron', label: 'Iron-deficiency pattern' },
      { key: 'vitD', id: 'toggle-vitd', label: 'Low vitamin D' },
      { key: 'esr', id: 'toggle-esr', label: 'Raised ESR' },
      { key: 'prediabetes', id: 'toggle-prediabetes', label: 'Add: prediabetes' },
    ],
  },
  {
    name: 'Lifestyle',
    items: [
      { key: 'active', id: 'toggle-active', label: 'Active 150 min a week' },
      { key: 'smoker', id: 'toggle-smoker', label: 'Smokes, under 10 a day' },
    ],
  },
  {
    name: 'Method',
    items: [{ key: 'plainAverage', id: 'toggle-plain-average', label: 'Show as plain average' }],
  },
];

function Switch({ id, label, pressed, onToggle }) {
  return (
    <button
      type="button"
      aria-pressed={pressed}
      className="hs-switch"
      onClick={onToggle}
      data-trapezium-id={`${SCREEN}.${id}`}
    >
      <span className="hs-switch__track" aria-hidden="true">
        <span className="hs-switch__thumb" />
      </span>
      <span className="hs-switch__label">{label}</span>
    </button>
  );
}

/* ---------- The five stages. What is not in play is dimmed, so the eye goes to what is. ---------- */

/* 1. The point tables, one body system at a time, with the patient's ranges lit. */
function PointsStage({ r, domainKey, onDomain }) {
  const domain = r.domains.find((d) => d.key === domainKey);
  return (
    <div className="hs-points">
      <div className="hs-domains" role="group" aria-label="Body system">
        {r.domains.map((d) => (
          <button
            key={d.key}
            type="button"
            aria-pressed={d.key === domainKey}
            className={`hs-domain${d.points > 0 ? ' is-flagged' : ''}`}
            onClick={() => onDomain(d.key)}
            data-trapezium-id={`${SCREEN}.domain-${d.key}`}
          >
            {d.name}
            <PtsBadge pts={d.points} />
          </button>
        ))}
      </div>
      <ul className="hs-ladders" aria-label={`${domain.name} point table`}>
        {domain.params.map((p) => (
          <Ladder
            key={p.key}
            name={p.name}
            unit={p.unit}
            value={p.value}
            segments={p.segments}
            index={p.index}
            pts={p.pts}
          />
        ))}
      </ul>
      <p className="hs-points__key">
        <span>Ranges for women</span>
        {domain.params.some((p) => p.segments.some((s) => s.uncovered)) && (
          <span className="hs-points__gap">Not scored by the framework</span>
        )}
      </p>
    </div>
  );
}

/* 2. Each body system: its points ÷ its maximum × 100. */
function SystemStage({ r }) {
  return (
    <ul className="hs-bigbars">
      {r.domains.map((d) => (
        <li
          key={d.key}
          className={d.points > 0 ? undefined : 'is-dim'}
          style={{ '--bar': d.points > 0 ? TIER_TONE.Suboptimal.fill : TIER_TONE.Good.fill }}
        >
          <span className="hs-bigbars__name">{d.name}</span>
          <span className="hs-bigbars__track" aria-hidden="true">
            <span style={{ width: `${Math.max(0, d.score)}%` }} />
          </span>
          <span className="hs-bigbars__frac">
            {d.points}/{d.max}
            <span className="sr-only"> points</span>
          </span>
          <span className="hs-bigbars__value">{one(d.score)}</span>
        </li>
      ))}
    </ul>
  );
}

/* 3. Weights: column width = weight, fill = system score; the area is what it adds. */
function WeightStage({ r }) {
  const plain = r.method === 'plain-average';
  return (
    <div className="hs-weights">
      <ol className="hs-mekko" aria-label={plain ? 'Each system counts one ninth' : 'System weights'}>
        {r.domains.map((d) => (
          <li
            key={d.key}
            style={{ flexGrow: d.share * 100, '--fill': `${Math.max(0, d.score)}%` }}
            className={d.points > 0 ? 'is-on' : undefined}
          >
            <span className="hs-mekko__add">
              {d.points > 0 ? `+${one(d.contribution)}` : ''}
              <span className="sr-only">{` ${d.name}: ${one(d.score)} × ${Math.round(d.share * 100)}%`}</span>
            </span>
            <span className="hs-mekko__col" aria-hidden="true">
              <span className="hs-mekko__name">{d.short}</span>
            </span>
            <span className="hs-mekko__w" aria-hidden="true">
              {Math.round(d.share * 100)}%
            </span>
          </li>
        ))}
      </ol>
      <p className="hs-sum">
        <span className="hs-sum__words">{plain ? 'Plain average ÷ 9' : 'Σ score × weight'}</span>
        <span className="hs-sum__big">{one(r.raw)}</span>
        <span className="hs-sum__words">Raw score</span>
      </p>
    </div>
  );
}

/* 4. Age, BMI, smoking and activity add (or take off) points. */
function AdjustStage({ r }) {
  return (
    <div className="hs-adjust">
      <ul className="hs-ladders">
        {ADJUSTMENTS.map((a) => {
          const got = r.adjustments.find((x) => x.key === a.key);
          return (
            <Ladder
              key={a.key}
              name={a.name}
              unit={a.unit}
              value={a.segments ? got.value : null}
              segments={a.segments || a.categories}
              index={got.index}
              pts={got.pts}
            />
          );
        })}
      </ul>
      <p className="hs-formula">
        <span className="hs-formula__nums">
          {one(r.raw)} <span className="hs-formula__op">{r.adjustTotal < 0 ? '−' : '+'}</span> {Math.abs(r.adjustTotal)}{' '}
          = <b>{one(r.final)}</b>
        </span>
        <span className="hs-formula__words">Raw + lifestyle = final</span>
      </p>
    </div>
  );
}

/* 5. The final score read as a category, with the framework's action. */
function TierStage({ r }) {
  const tone = TIER_TONE[r.tier];
  return (
    <div className="hs-tierstage">
      <div className="hs-tierstage__score">
        <ScoreRing value={r.score} label="Wellness Score" color={tone.fill} size={150} className="hs-ring" />
        <TierWord tier={r.tier} />
        <p className="hs-lower">Lower is better</p>
      </div>
      <div className="hs-tierstage__place">
        <BandBar score={r.score} className="hs-bands--big" />
        <dl className="hs-action" style={{ '--tone': tone.fill }}>
          <dt>Means</dt>
          <dd>{r.band.meaning}</dd>
          <dt>Action</dt>
          <dd>{r.band.action}</dd>
        </dl>
        {r.method === 'plain-average' && <p className="hs-vs">Weighted: {r.weightedScore}</p>}
      </div>
    </div>
  );
}

const STEPS = [
  {
    key: 'grade',
    tab: 'Points per result',
    icon: TestTubeGlyph,
    line: 'Every result earns points from its range',
    Stage: PointsStage,
  },
  {
    key: 'system',
    tab: 'Body systems',
    icon: LiverGlyph,
    line: 'Score each body system: points ÷ max × 100',
    Stage: SystemStage,
  },
  { key: 'weights', tab: 'Weights', icon: BarsGlyph, line: 'Weight each system, then add up', Stage: WeightStage },
  {
    key: 'adjust',
    tab: 'Lifestyle',
    icon: PersonGlyph,
    line: 'Add points for age, BMI, smoking and activity',
    Stage: AdjustStage,
  },
  { key: 'tier', tab: 'Category', icon: PillBarsGlyph, line: 'Read the category and its action', Stage: TierStage },
];

const tabId = (key) => `hs-step-tab-${key}`;
const PANEL_ID = 'hs-step-panel';

export default function ScoreSimulator() {
  const [findings, setFindings] = useState(DEFAULT_FINDINGS);
  const [active, setActive] = useState(0);
  const [domainKey, setDomainKey] = useState('nutrition');
  const r = computeScore(findings);
  const tone = TIER_TONE[r.tier];
  const step = STEPS[active];
  const { Stage } = step;

  const toggle = (key) => setFindings((f) => ({ ...f, [key]: !f[key] }));

  // Arrow keys move along the tabs (Home and End to the ends).
  const onKey = (e) => {
    const to = { ArrowRight: active + 1, ArrowLeft: active - 1, Home: 0, End: STEPS.length - 1 }[e.key];
    if (to === undefined) return;
    e.preventDefault();
    const next = (to + STEPS.length) % STEPS.length;
    setActive(next);
    document.getElementById(tabId(STEPS[next].key))?.focus();
  };

  return (
    <section className="card hs-flow" aria-labelledby="hs-flow-title">
      <h2 id="hs-flow-title" className="sr-only">
        How the score is worked out
      </h2>

      <div className="hs-tabs" role="tablist" aria-label="How the score is worked out">
        {STEPS.map(({ key, tab, icon: Icon }, i) => (
          <button
            key={key}
            type="button"
            role="tab"
            id={tabId(key)}
            aria-selected={i === active}
            aria-controls={PANEL_ID}
            tabIndex={i === active ? 0 : -1}
            className={`hs-tab${i === active ? ' is-active' : ''}`}
            onClick={() => setActive(i)}
            onKeyDown={onKey}
            data-trapezium-id={`${SCREEN}.step-${key}`}
          >
            <span className="hs-tab__num" aria-hidden="true">
              {i + 1}
            </span>
            <span className="hs-tab__icon" aria-hidden="true">
              <Icon size={28} />
            </span>
            <span className="hs-tab__name">{tab}</span>
          </button>
        ))}
      </div>

      <div className="hs-flow__body">
        <div
          className={`hs-stage hs-stage--${step.key}`}
          role="tabpanel"
          id={PANEL_ID}
          aria-labelledby={tabId(step.key)}
          tabIndex={0}
          data-trapezium-state={step.key}
        >
          <h3 className="hs-stage__line">{step.line}</h3>
          <div className="hs-stage__visual">
            <Stage r={r} domainKey={domainKey} onDomain={setDomainKey} />
          </div>
        </div>

        <aside className="hs-whatif" aria-labelledby="hs-sim-title">
          <h3 id="hs-sim-title" className="hs-whatif__title">
            What if we change some findings?
          </h3>
          {SWITCH_GROUPS.map((g) => (
            <div key={g.name} className="hs-switches" role="group" aria-label={g.name}>
              <p className="hs-switches__name" aria-hidden="true">
                {g.name}
              </p>
              {g.items.map((s) => (
                <Switch
                  key={s.key}
                  id={s.id}
                  label={s.label}
                  pressed={findings[s.key]}
                  onToggle={() => toggle(s.key)}
                />
              ))}
            </div>
          ))}
          <div className="hs-whatif__live" data-trapezium-state={`score-${r.score}`}>
            <p className="sr-only" role="status">
              {`Score ${r.score}, ${r.tier}. Lower is better.`}
            </p>
            <ScoreRing value={r.score} label="Live Wellness Score" color={tone.fill} size={72} className="hs-ring" />
            <div>
              <TierWord tier={r.tier} />
              <span className="hs-whatif__delta">{signed(r.score - DEFAULT_RESULT.score)} vs start</span>
            </div>
          </div>
        </aside>
      </div>
    </section>
  );
}
